<script setup>
import RecordLink from '@/components/record/RecordLink.vue'
import { computed } from 'vue'
import { ENTITIES } from '@/lib/entities.js'
import { formatMoney, formatDate } from '@/lib/format.js'
import { statusLabel, STATUS_TONES } from '@/lib/labels.js'
import UiBadge from '@/components/ui/UiBadge.vue'

/**
 * The opportunities a competitor is in (D-134), as on Dynamics' competitor card: each with its
 * stage, value and how it ended, and the win rate against it.
 */
const props = defineProps({ record: { type: Object, required: true } })
const counts = computed(() => props.record.opportunity_counts || { open: 0, won: 0, lost: 0 })
const winRate = computed(() => (counts.value.won + counts.value.lost ? Math.round((100 * counts.value.won) / (counts.value.won + counts.value.lost)) : null))
</script>

<template>
  <section class="card" data-testid="competitor-opportunities">
    <header class="flex flex-wrap items-center gap-3 border-b border-default px-3 py-2">
      <h3 class="flex-1 text-sm font-semibold">{{ $t('competitor.opportunities') }} <span class="font-normal text-faint">({{ (record.opportunities || []).length }})</span></h3>
      <span class="text-xs text-muted">{{ $t('competitor.counts', counts) }}</span>
      <UiBadge v-if="winRate !== null" :tone="winRate >= 50 ? 'green' : 'amber'">{{ $t('competitor.winRate', { n: winRate }) }}</UiBadge>
    </header>
    <p v-if="!(record.opportunities || []).length" class="px-3 py-3 text-sm text-faint">{{ $t('competitor.none') }}</p>
    <ul v-else class="divide-y divide-[var(--border)]">
      <li v-for="o in record.opportunities" :key="o.uuid" class="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
        <i :class="['fa-solid fa-fw text-xs', ENTITIES.opportunity.icon, ENTITIES.opportunity.color]" aria-hidden="true"></i>
        <RecordLink class="link min-w-0 flex-1 truncate" entity="opportunity" :uuid="o.uuid">{{ o.label }}</RecordLink>
        <span v-if="o.stage && o.status === 'open'" class="text-xs text-muted">{{ o.stage }}</span>
        <span v-if="o.estimated_value" class="text-xs tabular-nums text-muted">{{ formatMoney(o.estimated_value, o.currency_code) }}</span>
        <span v-if="o.estimated_close_date && o.status === 'open'" class="text-xs text-faint">{{ formatDate(o.estimated_close_date) }}</span>
        <UiBadge :tone="STATUS_TONES[o.status] || 'gray'">{{ statusLabel('opportunity', o.status) }}</UiBadge>
        <p v-if="o.note" class="w-full pl-6 text-xs text-muted">{{ o.note }}</p>
      </li>
    </ul>
  </section>
</template>
